import React, { useState, useEffect } from "react";
import {
  Button,
  Tag,
  Space,
  Drawer,
  Row,
  Col,
  Input,
  Select,
  DatePicker,
  Progress,
  Badge,
  Form,
} from "antd";
import type { TableColumnsType } from "antd";
import {
  FilterOutlined,
  CalendarOutlined,
  DollarOutlined,
  UserOutlined,
  ClockCircleOutlined,
  EyeOutlined,
  PhoneOutlined,
  SearchOutlined,
  ReloadOutlined,
  SaveOutlined,
  FolderOpenOutlined,
} from "@ant-design/icons";
import { Link, useNavigate, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import { Icon, TooltipComponent } from "../../components/Component";
import TransactionFlowDefaultTemplate from "../others/transactionFlowDefaultTemplate";
import RABillAside from "./raBillAside";
import { formatCurrency } from "../../utils/Utils";
import { mockRABills } from "./mockRABills";
import { useSelector } from "react-redux";
import dropdownApis from "../../api/WorkOrderManagement/DropdownApis";

const { Option } = Select;
const { RangePicker } = DatePicker;

// Filter State Interface
interface FilterState {
  siteId?: string[];
  contractorName?: string[];
  workOrderId?: string[];
}

const RABillsOutstanding: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get flowType from URL
  const searchParams = new URLSearchParams(location.search);
  const flowType = searchParams.get("flowType");
  const isClientFlow = flowType?.toLowerCase() === "client";
  
  const [sm, setSm] = useState(false);
  const [outstandingRABills, setOutstandingRABills] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
  const [filters, setFilters] = useState<FilterState>({});
  const [tempFilters, setTempFilters] = useState<FilterState>({});
  const [savedFilters, setSavedFilters] = useState<{
    [key: string]: FilterState;
  }>({});
  const [filterForm] = Form.useForm();
  const [contractorOptions, setContractorOptions] = useState<any[]>([]);
  const [siteOptions, setSiteOptions] = useState<any[]>([]);
  const [workOrderOptions, setWorkOrderOptions] = useState<any[]>([]);
  const { data: currentUser } = useSelector((state: any) => state.currentUser);

  useEffect(() => {
    loadOutstandingRABills();
  }, []);

  // Fetch site options from currentUser
  useEffect(() => {
    if (currentUser) {
      const { siteAccessibility } = currentUser;
      if (siteAccessibility) {
        const _siteOptions: any = [];
        siteAccessibility?.forEach((item: any) => {
          _siteOptions.push({ label: item.siteName, value: item.siteID });
        });
        setSiteOptions([..._siteOptions]);
      }
    }
  }, [currentUser]);

  // Count active filters
  const getActiveFilterCount = () => {
    let count = 0;
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        if (Array.isArray(value) && value.length > 0) count++;
        else if (typeof value === "string" && value.trim() !== "") count++;
      }
    });
    return count;
  };

  // Filter Functions
  const handleFilterDrawerOpen = async () => {
    setTempFilters({ ...filters });
    filterForm.setFieldsValue(filters);
    setFilterDrawerVisible(true);

    // Fetch dropdown data when drawer opens
    try {
      const flowTypeParam = isClientFlow ? "Client" : "Contractor";

      // Fetch contractors
      const partyType = isClientFlow ? "Client" : "Contractor";
      const contractorsResponse = await dropdownApis.fetchContractorDropDown(
        partyType
      );
      if (
        contractorsResponse &&
        contractorsResponse.status &&
        contractorsResponse.data
      ) {
        const contractorOptions = contractorsResponse.data.map((item: any) => ({
          label: item.companyName || "N/A",
          value: item.contractorID?.toString(),
        }));
        setContractorOptions(contractorOptions);
      }

      // Fetch work orders
      const workOrdersResponse = await dropdownApis.fetchWorkOrderDropdown(
        flowTypeParam
      );
      if (
        workOrdersResponse &&
        workOrdersResponse.status &&
        workOrdersResponse.data
      ) {
        const workOrderOptions = workOrdersResponse.data.map((item: any) => {
          const label =
            item.workOrderNumber ||
            item.workOrderSequenceId ||
            item.sequenceId ||
            item.workOrderTitle ||
            `WO-${item.workOrderId || item.id || "N/A"}`;
          const value = item.workOrderId?.toString() || item.id?.toString();
          return { label, value };
        });
        setWorkOrderOptions(workOrderOptions);
      }
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
    }
  };

  const handleFilterDrawerClose = () => {
    setFilterDrawerVisible(false);
    setTempFilters({});
    filterForm.resetFields();
  };

  const handleApplyFilters = () => {
    filterForm
      .validateFields()
      .then((values) => {
        const processedFilters: FilterState = {
          siteId:
            values.siteId && values.siteId.length > 0
              ? values.siteId
              : undefined,
          contractorName:
            values.contractorName && values.contractorName.length > 0
              ? values.contractorName
              : undefined,
          workOrderId:
            values.workOrderId && values.workOrderId.length > 0
              ? values.workOrderId
              : undefined,
        };

        // Remove undefined values
        Object.keys(processedFilters).forEach((key) => {
          if (processedFilters[key as keyof FilterState] === undefined) {
            delete processedFilters[key as keyof FilterState];
          }
        });

        setFilters(processedFilters);
        setFilterDrawerVisible(false);
      })
      .catch((error) => {
        console.error("Form validation failed:", error);
      });
  };

  const handleResetFilters = () => {
    setFilters({});
    setTempFilters({});
    filterForm.resetFields();
  };

  const handleSaveFilter = () => {
    const filterName = prompt("Enter a name for this filter:");
    if (filterName && filterName.trim()) {
      setSavedFilters((prev) => ({
        ...prev,
        [filterName.trim()]: { ...filters },
      }));
    }
  };

  const handleLoadFilter = (filterName: string) => {
    const savedFilter = savedFilters[filterName];
    if (savedFilter) {
      setFilters(savedFilter);
      setTempFilters(savedFilter);
      filterForm.setFieldsValue(savedFilter);
    }
  };

  const loadOutstandingRABills = () => {
    setLoading(true);
    try {
      // Filter RA Bills that have outstanding payments (Unpaid or Partially Paid)
      const outstandingBills = mockRABills
        .filter(
          (bill) =>
            ["Approved", "Posted", "Paid"].includes(bill.status) &&
            bill.balanceAmount > 0
        )
        .map((bill) => ({
          ...bill,
          // Calculate days since approval for aging analysis
          daysSinceApproval: bill.approvedOn
            ? dayjs().diff(dayjs(bill.approvedOn), "days")
            : 0,
          // Calculate expected payment date (30 days from approval)
          expectedPaymentDate: bill.approvedOn
            ? dayjs(bill.approvedOn).add(30, "days").format("YYYY-MM-DD")
            : null,
          // Check if overdue
          isOverdue: bill.approvedOn
            ? dayjs().diff(dayjs(bill.approvedOn), "days") > 30
            : false,
        }));

      setOutstandingRABills(outstandingBills);
    } catch (error) {
      console.error("Error loading outstanding RA Bills:", error);
    } finally {
      setLoading(false);
    }
  };

  const getPaymentStatusColor = (status: string): string => {
    const statusColors: { [key: string]: string } = {
      "Partially Paid": "warning",
      Unpaid: "error",
    };
    return statusColors[status] || "default";
  };

  const getAgingColor = (days: number): string => {
    if (days > 60) return "#ff4d4f";
    if (days > 30) return "#faad14";
    return "#52c41a";
  };

  const getPaymentProgress = (
    paidAmount: number,
    totalAmount: number
  ): number => {
    // Handle null, undefined, or NaN values
    const safePaidAmount = Number(paidAmount) || 0;
    const safeTotalAmount = Number(totalAmount) || 0;

    if (safeTotalAmount === 0) return 0;

    const percentage = Math.round((safePaidAmount / safeTotalAmount) * 100);

    // Ensure the result is a valid number
    return isNaN(percentage) ? 0 : percentage;
  };

  const handleViewRABill = (record: any) => {
    const searchParams = new URLSearchParams(location.search);
    const flowType = searchParams.get("flowType");
    const flowTypeQuery = flowType ? `&flowType=${flowType}` : "";
    navigate(
      `${process.env.PUBLIC_URL}/ra-bills-view-details/${record.raBillId}?mode=view${flowTypeQuery}`
    );
  };

  const handleFollowUp = (record: any) => {
    // Implementation for follow-up action (email, notification, etc.)
    console.log("Follow up for:", record.raBillNumber);
  };


  // KPI calculations
  const totalOutstanding = outstandingRABills.length;
  const totalOutstandingValue = outstandingRABills.reduce(
    (sum, bill) => sum + (Number(bill.balanceAmount) || 0),
    0
  );
  const overdueCount = outstandingRABills.filter(
    (bill) => bill.isOverdue
  ).length;
  const avgAgingDays =
    outstandingRABills.length > 0
      ? Math.round(
          outstandingRABills.reduce(
            (sum, bill) => sum + (Number(bill.daysSinceApproval) || 0),
            0
          ) / outstandingRABills.length
        )
      : 0;

  const columns: TableColumnsType<any> = [
    {
      title: "RA Bill #",
      dataIndex: "raBillSequenceId",
      key: "raBillSequenceId",
      width: 220,
      fixed: "left",
      render: (text: string, record: any) => (
        <div>
          <span
            className="inlineTableCellLink"
            id={`raBillOutstanding-${record?.raBillId}`}
            onClick={() => handleViewRABill(record)}
          >
            {text}
          </span>
          {record.isOverdue && (
            <div>
              <Tag color="red">OVERDUE</Tag>
            </div>
          )}
        </div>
      ),
    },
    {
      title: "WO #",
      dataIndex: "workOrderNumber",
      key: "workOrderNumber",
      width: 220,
      // render: (text, record) => (
      //   <div>
      //     <div className="fw-bold text-primary">{text}</div>
      //     <div className="text-muted small mt-1">{record.contractor}</div>
      //   </div>
      // ),
    },
    {
      title: "Title",
      dataIndex: "workOrderTitle",
      key: "workOrderTitle",
      width: 250,
      render: (text: string, record: any, index: any) => {
        return (
          <>
            <span id={`title-tooltip-${index}`}>
              {text?.length > 30 ? `${text?.substring(0, 30)}... ` : text}
            </span>
            {text?.length > 30 && (
              <TooltipComponent
                iconClass="info-circle text-info"
                icon="info"
                id={`title-tooltip-${index}`}
                direction="top"
                text={text}
                containerClassName={""}
              />
            )}
          </>
        );
      },
    },
    {
      title: "Site/Project",
      key: "siteProject",
      width: 200,
      render: (_, record) => (
        <div>
          <div className="fw-bold">{record.siteName}</div>
          <div className="text-muted small">{record.projectName}</div>
        </div>
      ),
    },
    {
      title: "RA Bill Value",
      dataIndex: "netPayable",
      key: "netPayable",
      width: 140,
      align: "right",
      render: (value) => (
        <div className="fw-bold">{formatCurrency(value, "INR")}</div>
      ),
    },
    {
      title: "Paid Amount",
      dataIndex: "paidAmount",
      key: "paidAmount",
      width: 130,
      align: "right",
      render: (paidAmount, record) => (
        <div>
          <div className="fw-bold text-success">
            {formatCurrency(paidAmount, "INR")}
          </div>
          <div className="text-muted small">
            {(() => {
              const percentage = getPaymentProgress(
                paidAmount,
                record.netPayable
              );
              return `${percentage}% paid`;
            })()}
          </div>
        </div>
      ),
    },
    {
      title: "Outstanding Amount",
      dataIndex: "balanceAmount",
      key: "balanceAmount",
      width: 180,
      align: "right",
      render: (balance, record) => (
        <div>
          <div className="fw-bold text-danger">
            {formatCurrency(balance, "INR")}
          </div>
          <Progress
            percent={getPaymentProgress(
              record.paidAmount,
              record.balanceAmount
            )}
            size="small"
            strokeColor="#ff4d4f"
            showInfo={false}
          />
        </div>
      ),
      sorter: (a, b) => a.balanceAmount - b.balanceAmount,
    },
    {
      title: "Approved Date",
      dataIndex: "approvedAt",
      key: "approvedAt",
      width: 130,
      render: (date) => (date ? dayjs(date).format("DD-MMM-YYYY") : "---"),
      sorter: (a, b) => dayjs(a.approvedAt).unix() - dayjs(b.approvedAt).unix(),
    },
    {
      title: "Expected Payment",
      key: "paymentDueDate",
      width: 140,
      render: (_, record) => (
        <div>
          {record.paymentDueDate ? (
            <div className={record.isOverdue ? "text-danger" : ""}>
              {dayjs(record.paymentDueDate).format("DD-MMM-YYYY")}
            </div>
          ) : (
            <span className="text-muted">---</span>
          )}
        </div>
      ),
    },
    {
      title: "Aging (Days)",
      key: "slaAgingForOutstanding",
      dataIndex: "slaAgingForOutstanding",
      width: 120,
      align: "center",
      render: (_, record) => {
        const agingDays = record.slaAgingForOutstanding || 0;
        return (
          <Badge count={`${agingDays}d`} color={getAgingColor(agingDays)} />
        );
      },
      sorter: (a, b) =>
        (a.slaAgingForOutstanding || 0) - (b.slaAgingForOutstanding || 0),
    },
    {
      title: "Payment Status",
      dataIndex: "paymentStatus",
      key: "paymentStatus",
      width: 130,
      render: (status) => (
        <Tag color={getPaymentStatusColor(status)}>{status}</Tag>
      ),
    },
    // {
    //   title: "Actions",
    //   key: "actions",
    //   fixed: "right",
    //   width: 180,
    //   render: (_, record) => (
    //     <Space wrap size="small">
    //       <Button
    //         type="primary"
    //         size="small"
    //         icon={<EyeOutlined />}
    //         onClick={() => handleViewRABill(record)}
    //       >
    //         View
    //       </Button>
    //       <Button
    //         size="small"
    //         icon={<PhoneOutlined />}
    //         onClick={() => handleFollowUp(record)}
    //       >
    //         Follow Up
    //       </Button>
    //     </Space>
    //   ),
    // },
  ];

  return (
    <>
    <TransactionFlowDefaultTemplate
      moduleKey="ra-bills-outstanding"
      title="Outstanding RA Bills"
      subtitle="Track and manage unpaid and partially paid Running Account Bills"
      columns={columns}
      dataSource={outstandingRABills}
      loading={loading}
      customFilters={filters}
      sideNavigation={
        <div
          className={`card-aside card-aside-left user-aside toggle-slide toggle-slide-left toggle-break-lg ${
            sm ? "content-active" : ""
          }`}
        >
          <RABillAside updateSm={setSm} sm={sm} />
        </div>
      }
      topRightJSX={
        <Space>
          <Badge count={getActiveFilterCount()} size="small">
            <Button
              type="primary"
              icon={<FilterOutlined />}
              onClick={handleFilterDrawerOpen}
            >
              Advanced Filters
            </Button>
          </Badge>
        </Space>
      }
      extra={{
        tableScrollX: 1800,
        showSearch: true,
        searchPlaceholder: "Search by RA Bill#, WO#, or Contractor...",
        kpiWidgets: [
          {
            title: "Total Outstanding Bills",
            value: totalOutstanding,
            icon: <ClockCircleOutlined />,
            color: "#faad14",
          },
          {
            title: "Outstanding Value",
            value: formatCurrency(totalOutstandingValue, "INR"),
            icon: <DollarOutlined />,
            color: "#ff4d4f",
          },
          {
            title: "Overdue Bills",
            value: overdueCount,
            icon: <CalendarOutlined />,
            color: "#ff4d4f",
          },
          {
            title: "Average Aging",
            value: `${avgAgingDays} days`,
            icon: <UserOutlined />,
            color: "#722ed1",
          },
        ],
      }}
    />

      {/* Advanced Filters Drawer */}
      <Drawer
        title="Advanced Filters"
        width={420}
        open={filterDrawerVisible}
        onClose={handleFilterDrawerClose}
        extra={
          <Space size="middle">
            <Badge count={getActiveFilterCount()} size="small">
              <Button size="small" type="text" icon={<FilterOutlined />} />
            </Badge>
            <Button
              size="small"
              onClick={handleResetFilters}
              icon={<ReloadOutlined />}
            >
              Reset
            </Button>
          </Space>
        }
        footer={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Space>
              <Button
                size="small"
                onClick={handleSaveFilter}
                icon={<SaveOutlined />}
              >
                Save Filter
              </Button>
              {Object.keys(savedFilters).length > 0 && (
                <Select
                  placeholder="Load saved filter"
                  size="small"
                  style={{ width: 150 }}
                  suffixIcon={<FolderOpenOutlined />}
                  onChange={handleLoadFilter}
                  allowClear
                >
                  {Object.keys(savedFilters).map((filterName) => (
                    <Option key={filterName} value={filterName}>
                      {filterName}
                    </Option>
                  ))}
                </Select>
              )}
            </Space>
            <Space>
              <Button onClick={handleFilterDrawerClose}>Cancel</Button>
              <Button
                type="primary"
                onClick={handleApplyFilters}
                icon={<SearchOutlined />}
              >
                Apply Filters
              </Button>
            </Space>
          </div>
        }
      >
        <Form form={filterForm} layout="vertical" initialValues={filters}>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Form.Item name="siteId" label="Site/Project">
                <Select
                  mode="multiple"
                  placeholder="Select Sites"
                  options={siteOptions}
                  allowClear
                  showSearch
                  filterOption={(input, option: any) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name="contractorName"
                label={isClientFlow ? "Client" : "Contractor"}
              >
                <Select
                  mode="multiple"
                  placeholder={
                    isClientFlow ? "Select Clients" : "Select Contractors"
                  }
                  options={contractorOptions}
                  allowClear
                  showSearch
                  filterOption={(input, option: any) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="workOrderId" label="Work Order">
                <Select
                  mode="multiple"
                  placeholder="Select Work Orders"
                  options={workOrderOptions}
                  allowClear
                  showSearch
                  filterOption={(input, option: any) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Drawer>
    </>
  );
};

export default RABillsOutstanding;
